import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from '../../icons';
import type { Product } from './mockProducts';
import { COLOR_HEX } from './mockProducts';
import styles from './ProductList.module.css';

const PER_PAGE = 15;

interface ProductListProps {
  products: Product[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  selectedCount: number;
}

export function ProductList({
  products,
  selected,
  onToggle,
  onToggleAll,
  search,
  onSearchChange,
  selectedCount,
}: ProductListProps) {
  const { t } = useTranslation('catalogue');
  const [page, setPage] = useState(0);
  const [added, setAdded] = useState(false);

  useEffect(() => setPage(0), [products.length, search]);

  const handleAdd = useCallback(() => {
    if (selectedCount === 0) return;
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  }, [selectedCount]);

  const totalPages = Math.max(1, Math.ceil(products.length / PER_PAGE));
  const pageProducts = useMemo(
    () => products.slice(page * PER_PAGE, (page + 1) * PER_PAGE),
    [products, page]
  );

  const allSelected = pageProducts.length > 0 && pageProducts.every(p => selected.has(p.id));
  const hasSelection = selectedCount > 0;

  const btnClass = [
    styles.addButton,
    !hasSelection && styles.addButtonDisabled,
    added && styles.addButtonSuccess,
  ].filter(Boolean).join(' ');

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <div className={styles.searchBar}>
          <SearchIcon size={16} color="currentColor" />
          <input
            className={styles.searchInput}
            type="text"
            placeholder={t('demo.searchPlaceholder')}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
          />
        </div>
        <button className={btnClass} onClick={handleAdd} disabled={!hasSelection && !added}>
          {added ? (
            <>✓ {t('demo.added')}</>
          ) : (
            <>
              {hasSelection && (
                <span className={styles.selectionCount}>{selectedCount}</span>
              )}
              {t('demo.addToFeed')}
              <span className={styles.arrow}>→</span>
            </>
          )}
        </button>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              <th>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={allSelected}
                  onChange={onToggleAll}
                />
              </th>
              <th>{t('demo.columns.model')}</th>
              <th>{t('demo.columns.brand')}</th>
              <th>{t('demo.columns.year')}</th>
              <th>{t('demo.columns.storage')}</th>
              <th>{t('demo.columns.grade')}</th>
              <th>{t('demo.columns.color')}</th>
              <th>{t('demo.columns.stock')}</th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {pageProducts.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyState}>
                  {t('demo.noResults')}
                </td>
              </tr>
            ) : (
              pageProducts.map(product => {
                const isSelected = selected.has(product.id);
                const rowClass = `${styles.row}${isSelected ? ` ${styles.selected}` : ''}`;
                return (
                  <tr key={product.id} className={rowClass} onClick={() => onToggle(product.id)}>
                    <td>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={isSelected}
                        onChange={() => onToggle(product.id)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                    <td className={styles.model}>{product.model}</td>
                    <td>{product.brand}</td>
                    <td className={styles.mobileHidden}>{product.year}</td>
                    <td className={styles.mobileHidden}>{product.storage}</td>
                    <td>
                      <span className={styles.gradeBadge} data-grade={product.grade}>
                        {product.grade}
                      </span>
                    </td>
                    <td className={`${styles.colorCell} ${styles.mobileHidden}`}>
                      <span
                        className={styles.colorDot}
                        style={{ background: COLOR_HEX[product.color] || '#6b7280' }}
                      />
                      {product.color}
                    </td>
                    <td className={`${styles.stock} ${styles.mobileHidden}`}>{product.stock}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`${styles.pageBtn}${i === page ? ` ${styles.pageBtnActive}` : ''}`}
              onClick={() => setPage(i)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className={styles.pageBtn}
            disabled={page === totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            ›
          </button>
          <span className={styles.pageInfo}>
            {products.length} {t('demo.pagination.results')}
          </span>
        </div>
      )}
    </div>
  );
}
